import { IContextualMenuItem, IContextualMenuProps } from '@fluentui/react';
import { IconButton } from "@fluentui/react/lib/Button";
import { Box, Button, Divider, Typography } from '@mui/material';
import * as React from 'react';
import { useTranslation } from "react-i18next";
import { gridIcon } from "../../assets/icons";
import { GridContext } from '../../context/GridProvider/GridContext';
import { GridContextType } from '../../context/GridProvider/GridContextTypes';
import { CanShow } from "../../pages/WorkSpaceItem/WorkSpaceItemListStyle/CanShow";
import { createdAt, description, getDefault, size, tags, term, validationRule } from "../../pages/WorkSpaceItem/WorkSpaceItemListStyle/ColumnStandard";
import CheckboxComp from "../Checkbox/CheckboxComp";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Add, Check } from '@mui/icons-material';
interface IChooseColumns {
}

const ChooseColumns: React.ForwardRefExoticComponent<IChooseColumns> = React.forwardRef(({ }, ref) => {
  const { t, i18n } = useTranslation();
  const { columns, addColumn, removeColumn, persistColumn } = React.useContext(GridContext) as GridContextType;

  const menuItems = [
    { item: description, name: t('ChooseColumns.description') },
    { item: validationRule, name: t("ChooseColumns.validationRule") },
    { item: createdAt, name: t('ChooseColumns.createdAt') },
    { item: tags, name: 'Tags' },
    { item: size, name: t('ChooseColumns.size') },
    { item: term, name: t('ChooseColumns.term') }
  ];

  const [tempVisibility, setTempVisibility] = React.useState<Record<string, boolean>>(() => {
    return menuItems.reduce((acc, { item }) => {
      acc[item] = CanShow(columns, item); 
      return acc;
    }, {} as Record<string, boolean>);
  });

  const allChecked = Object.values(tempVisibility).every(value => value === true);

  const changeColumnVisibility = (name: string, checked: boolean) => {
    setTempVisibility((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const reset = () => {
    setTempVisibility(() => {
      return menuItems.reduce((acc, { item }) => {
        acc[item] = true; 
        return acc;
      }, {} as Record<string, boolean>);
    });
  };

  const applyFilter = () => {
    Object.entries(tempVisibility).forEach(([name, isVisible]) => {
      if (isVisible) {
        addColumn({ name, length: getDefault(name) });
      } else {
        removeColumn(name);
      }
    });
    persistColumn(); 
  };

  const handleMenuHeader = (): IContextualMenuItem => {
    return {
      key: "header",
      onRender: () => (
        <>
          <Comp>
            <Box sx={{ height: '32px', margin: '3px 11px 0px' }}>
              <Typography sx={{ font:"normal normal 600 18px/27px Segoe UI"}}>
                {t('ChooseColumns.message')}
              </Typography>
            </Box>
          </Comp>
          <Divider/>
        </>
      )
    };
  };

  const handleMenuFooter = (): IContextualMenuItem => {
    return {
      key: "footer",
      onRender: () => (
          <Comp>
            <Box sx={{display:"flex", justifyContent:'space-between', padding: '11px'}}>
            <Button 
                onClick={reset}
                disabled={allChecked}
                variant='outlined' 
                sx={{ 
                  textTransform: 'none', 
                  font: 'normal normal normal 16px/21px Segoe UI', 
                  border: '1px solid #22638D ', 
                  borderRadius: '4px', 
                  color: '#22638D'
              }}>
              {t("ChooseColumns.reset")}
            </Button>

            <Button 
            onClick={applyFilter}
            startIcon={<Check/>} 
            variant='contained' 
            sx={{ 
                textTransform: 'none', 
                font: 'normal normal normal 16px/21px Segoe UI', 
                borderRadius: '4px', 
                color: '#ffff', 
                background:"#22638D" 
            }}>
              {t("ChooseColumns.applyFilter")}
            </Button>
            </Box>
          </Comp>

      )
    };
  };

  const handleMenuItem = (nameColumn: string, name: string): IContextualMenuItem => {
    return {
      key: nameColumn,
      onRender: () => (
        <>
          <Comp>
            <CheckboxComp
              label={name}
              checked={tempVisibility[nameColumn] ?? false}
              onChange={(ev, checked) => {
                if (checked !== undefined) {
                  changeColumnVisibility(nameColumn, checked);
                }
              }}
              />
          </Comp>
          <Divider/>
        </>
      )
    };
  };

  const sortMenuItems = (items: any[]) => {
    return items.slice().sort((a, b) => {
      const itemA = a.name;
      const itemB = b.name;
      return itemA.localeCompare(itemB);
    });
  };

  const mappedItems = sortMenuItems(menuItems).map(({ item, name }) =>
    handleMenuItem(item, name)
  );

  const handleMenuProps = (): IContextualMenuProps => {
    return {
      items: [handleMenuHeader(), ...mappedItems, handleMenuFooter()],
      directionalHintFixed: true,
      onItemClick: (ev, item) => {
        ev?.preventDefault();
      },
      styles: {
        root: {
          width: '334px',
          border: '1px solid #22638D',
          borderRadius: '4px',
          
        },
        subComponentStyles: {
          callout: {
            root: {
              marginTop: '4px',
              marginRight: '68px'
            },
          },
        },
      },
    };
  };

  return (
    <IconButton
      style={{
        height: '38px',
        border: '1px solid #22638D',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#22638D',
        padding: '0px 8px'
      }}
      menuProps={handleMenuProps()}
      styles={{
        menuIcon: {
          display: "none",
        },
        
      }}
    >
      <FilterAltIcon sx={{marginRight: '8px'}}/>
      {t("ChooseColumns.message")}
    </IconButton>
  );
});

interface CompProp {
  children: React.ReactNode;
}

const Comp: React.FC<CompProp> = ({ children }: CompProp) => (
  <div
    style={{
      padding: "5px"
    }}
  >
    {children}

  </div>
);

export default ChooseColumns;
